<template>
  <main class="px-4">
    <h1 class="my-6 text-center text-2xl">Select your host</h1>
    <div>
      <section v-if="siteEmployees?.length !== 0">
        <ComboboxInput
          :allow-multi-selection="true"
          :disabled="isLoading"
          :is-valid="errorMessage === ''"
          :options="siteEmployees"
          :required-characters-for-filter="2"
          identifier="visitor-arrival-employee-selection"
          label="Who are you visiting?"
          placeholder="Start typing to search..."
          @option-changed="employeeSelected"
          @max-selection-reached="maxSelectionReached"
        >
          <span v-if="errorMessage" class="error m-1 !my-0 block">
            {{ errorMessage }}
          </span>
        </ComboboxInput>
      </section>
      <section v-else>
        <p>
          There is no available site employee. Please speak to the site
          representative.
        </p>
      </section>
    </div>
    <div>
      <PrimaryButton
        :key="'visitor-arrival-continue-button-' + continueButtonKey"
        :disabled="
          siteEmployees?.length !== 0 && selectedSiteEmployees?.length === 0
        "
        action="Continue"
        class="mt-4"
        identifier="accreditation-form-continue"
        @click.once="notifySiteEmployees"
      />
    </div>
    <LoadingView :is-loading="isLoading" message="Sending notifications..." />
  </main>
</template>

<script lang="ts" setup>
  import { onMounted, ref } from 'vue'
  import ComboboxInput from '@/components/ComboboxInput.vue'
  import PrimaryButton from '@/components/PrimaryButton.vue'
  import {
    retrieveSiteEmployees,
    saveVisitorArrival,
    SiteEmployeesType,
  } from '@/services/api/visitorArrival'
  import { storeToRefs } from 'pinia'
  import { useUserStore } from '@/storage/user'
  import { useSiteStore } from '@/storage/site'
  import { useRouter } from 'vue-router'
  import LoadingView from '@/views/LoadingView.vue'
  import { useUserVisitorCompaniesStore } from '@/storage/userVisitorCompanies'
  import { useSessionStore } from '@/storage/session'

  const { isLoggedIn, userType, selectedContractorCompanyName } =
    storeToRefs(useUserStore())
  const { id: siteId } = storeToRefs(useSiteStore())
  const { lastSelected: visitorCompanyName } = storeToRefs(
    useUserVisitorCompaniesStore(),
  )
  const { addSessionEvent } = useSessionStore()
  const { currentEvent } = storeToRefs(useSessionStore())

  const selectedSiteEmployees = ref<{ id: number; name: string }[]>([])
  const siteEmployees = ref<{ id: number; name: string }[]>([])
  const errorMessage = ref('')
  const isLoading = ref(false)
  const router = useRouter()
  const continueButtonKey = ref(0)

  isLoading.value = true
  await getSiteEmployees()
  isLoading.value = false

  onMounted(() => {
    if (isLoggedIn.value && currentEvent.value !== 'VisitorArrivalEvent') {
      addSessionEvent('VisitorArrivalEvent')
    }
  })

  async function getSiteEmployees() {
    const siteEmployeeData = await retrieveSiteEmployees(
      siteId.value,
      userType.value.id,
    )

    if (typeof siteEmployeeData === 'boolean' && !siteEmployeeData) {
      await router.replace({ name: 'siteEntryConfirmation' })
      return
    }

    siteEmployees.value = siteEmployeeData.map(
      (employee: SiteEmployeesType) => {
        return {
          id: employee.id,
          name: `${employee.firstName} ${employee.lastName}`,
        }
      },
    )
  }

  function employeeSelected(employee: { id: number; name: string }[]) {
    selectedSiteEmployees.value = employee
  }

  async function notifySiteEmployees() {
    continueButtonKey.value += 1
    if (errorMessage.value !== '') {
      return
    }

    const employeeIds = selectedSiteEmployees.value.map(
      (employee) => employee.id,
    )

    isLoading.value = true

    const currentCompany =
      selectedContractorCompanyName.value !== ''
        ? selectedContractorCompanyName.value
        : visitorCompanyName.value

    await saveVisitorArrival(
      siteId.value,
      currentCompany,
      employeeIds,
      userType.value.id,
      siteEmployees.value?.length === 0,
    )
    await router.replace({ name: 'siteEntryConfirmation' })

    isLoading.value = false
  }

  function maxSelectionReached(limitReached: boolean): void {
    if (limitReached) {
      errorMessage.value = `Up to 5 employees can be selected.`
      return
    }

    errorMessage.value = ''
  }
</script>
